import _find from 'lodash/find';
import _round from 'lodash/round';
import dayjs from 'dayjs';

import { createVATPrice, createVATComboPrice } from '@web-solutions/core/utils/vat';
//@ts-ignore
import { COUNTRIES } from '@web-solutions/module-billing/utils';

import { Period, Product, ProductInfo, CURRENCY_SYMBOL_BY_CODE, Currency } from '../constants';
import { COUNTRY_VAT } from '@web-solutions/core/payment/constants';
import { ProductDetails } from '@web-solutions/core/store/billing/selectors';

let DEFAULT_CURRENCY_FOR_UPSALE: Currency;

export const getPreparedPrice = (price: number) => {
  const roundedPrice = _round(price, 2);
  return Number.isInteger(roundedPrice)
    ? Math.trunc(roundedPrice)
    : roundedPrice;
};

export const formatPriceForDisplay = (price: number, currency: Currency) => {
  const preparedPrice = getPreparedPrice(price);

  if (currency === 'JPY') {
    return Math.trunc(preparedPrice) + '';
  }

  return Number.isInteger(price)
    ? preparedPrice.toString()
    : preparedPrice.toFixed(2);
};

export const convertFromUSDWithVAT = (amountUSD: number, currency: Currency, country: string, usdConversionRates: Record<Currency, number>) => {
  const rate = usdConversionRates[currency]

  if (!rate || currency === 'USD') {
    return amountUSD;
  }

  const { vat: vatPercent } = COUNTRY_VAT?.[country] || { vat: 0 };
  const convertPrice = amountUSD * rate

  if (vatPercent) {
    const vat = convertPrice * (vatPercent / 100)

    return convertPrice + vat;
  }

  return convertPrice
}

export const getPreparedWeekPrice = (period: Period, amount: number): number => {
  let result = amount;

  switch (period) {
    case 'P2W':
      result = amount / 2;
      break;
    case 'P3W':
      result = amount / 3;
      break;
    case 'P1M':
      result = amount / 4;
      break;
    case 'P2M':
      result = amount / 8;
      break;
    case 'P3M':
      result = amount / 13;
      break;
    case 'P6M':
      result = amount / 26;
      break;
    case 'P1Y':
      result = amount / 52;
      break;
    default:
      result = Number(amount);
  }

  return _round(result, 2);
};

export const getPreparedDayPrice = (period: Period, amount: number): number => {
  let result = amount;

  switch (period) {
    case 'P1W':
      result = amount / 7;
      break;
    case 'P2W':
      result = amount / 7 / 2;
      break;
    case 'P3W':
      result = amount / 7 / 3;
      break;
    case 'P1M':
      result = amount / 30;
      break;
    case 'P2M':
      result = amount / 30 / 2;
      break;
    case 'P3M':
      result = amount / 30 / 3;
      break;
    case 'P6M':
      result = amount / 30 / 6;
      break;
    case 'P1Y':
      result = amount / 365;
      break;
    default:
      result = Number(amount);
  }

  return _round(result, 2);
};

export const getPreparedPeriod = (period: string): Period => {
  switch (period) {
    case 'P7D':
      return 'P1W';
    case 'P14D':
      return 'P2W';
    case 'P30D':
      return 'P1M';
    case 'P60D':
      return 'P2M';
    case 'P90D':
      return 'P3M';
    case 'P180D':
      return 'P6M';
    default:
      return period as Period;
  }
};

export const getCurrencySymbols = (currency: string) => {
  const currencySymbol: string = CURRENCY_SYMBOL_BY_CODE[currency?.toLowerCase?.() as keyof typeof CURRENCY_SYMBOL_BY_CODE] ?? currency;
  let currencySuffix = '';

  if (currency === 'AUD' || currency === "CAD") {
    currencySuffix = ` ${currency}`;
  }

  return { currencySymbol, currencySuffix }
}

function getPrevPrice(amount: number, discount: number) {
  return _round(_round(+amount * 100 / (100 - discount), 1) - 0.01, 2);
}

function calculateDiscount(oldPrice: number, newPrice: number) {
  const discount = ((oldPrice - newPrice) / oldPrice) * 100;
  return Math.ceil(discount);
}

type MappingPricesToProductsParams = {
  products: any[],
  prices: Product[],
  countryCode: string,
  displayVAT?: boolean
  usdConversionRates: Record<Currency, number>,
}

type MappingPricesToUpsaleProductsParams = {
  products: Record<string, ProductDetails[]>,
  countryCode: string,
  displayVAT?: boolean,
  usdConversionRates: Record<Currency, number>,
}

export const mappingPricesToProducts = ({ products, prices, countryCode, displayVAT, usdConversionRates }: MappingPricesToProductsParams): ProductInfo[] => {
  const country = COUNTRIES[countryCode as keyof typeof COUNTRIES];

  const defaultProduct = _find(products, { default: true });
  const defaultPrice = _find(prices, { id: defaultProduct?.id });
  const currencyProduct = displayVAT && _find(defaultPrice?.currencies, i => i.country === country || i.country.includes(country))
  const { currency: defaultCurrency } = currencyProduct || defaultPrice || {};

  DEFAULT_CURRENCY_FOR_UPSALE = defaultCurrency || 'USD'

  return products.reduce((pds, item) => {
    const price = prices.find(({ id }) => id === item.id) as ProductInfo;

    if (price) {
      const { discount, oldTrialPriceAmount } = item;
      const convertOldTrialPriceAmount = convertFromUSDWithVAT(
        +oldTrialPriceAmount,
        DEFAULT_CURRENCY_FOR_UPSALE,
        countryCode.toLowerCase(),
        usdConversionRates
      );

      const { trial_period_days } = price;
      const currencyProduct =  displayVAT && _find(price.currencies, i => (i.country === country || i.country.includes(country)) && i.currency === defaultCurrency)
      const { amount, currency, trial_price_amount } = currencyProduct || price;

      const { currencySymbol, currencySuffix } = getCurrencySymbols(currency);

      const period = getPreparedPeriod(price.period || item.period);

      price.priceTrialUSD = price.trial_price_amount;
      price.priceSubUSD = price.amount;

      price.trialDays = +trial_period_days;
      price.trialPriceAmount = +trial_price_amount;
      price.trialPriceText = `${currencySymbol}${getPreparedPrice(+trial_price_amount)}${currencySuffix}`;
      price.trialDayPrice = `${currencySymbol}${_round(+trial_price_amount / +trial_period_days, 2)}${currencySuffix}`;

      price.oldTrialPriceAmount = +(formatPriceForDisplay(convertOldTrialPriceAmount, currency));
      price.oldTrialPriceText = `${currencySymbol}${price.oldTrialPriceAmount}`;
      price.savedTrialPriceText = `${currencySymbol}${+(Number(price.oldTrialPriceAmount) - price.trialPriceAmount).toFixed(2)}${currencySuffix}`;
      price.perDayOldPriceAmount = +(Number(item.oldTrialPriceAmount) / Number(price.trialDays)).toFixed(2);
      price.discount = calculateDiscount(price.oldTrialPriceAmount, price.trialPriceAmount)

      price.isTrial = price.trialDays > 0;
      price.isIntroductory = price.trialPriceAmount > 0;

      price.price = price.priceText = `${currencySymbol}${getPreparedPrice(+amount)}${currencySuffix}`;
      price.priceAmount = getPreparedPrice(+amount);
      price.prevPriceAmount = getPrevPrice(+amount, price.discount);
      price.prevPrice = `${currencySymbol}${price.prevPriceAmount}${currencySuffix}`;
      price.savedPrice = `${currencySymbol}${getPreparedPrice(price.prevPriceAmount - +amount)}${currencySuffix}`;

      price.weekPriceAmount = getPreparedWeekPrice(period, +amount);
      price.weekPrice = `${currencySymbol}${price.weekPriceAmount}${currencySuffix}`;
      price.prevWeekPriceAmount = getPrevPrice(+price.weekPriceAmount, price.discount);
      price.prevWeekPrice = `${currencySymbol}${price.prevWeekPriceAmount}${currencySuffix}`;
      price.savedWeekPrice = `${currencySymbol}${getPreparedPrice(price.prevWeekPriceAmount - price.weekPriceAmount)}${currencySuffix}`;

      price.dayPriceAmount = getPreparedDayPrice(period, +amount);
      price.dayPrice = `${currencySymbol}${price.dayPriceAmount}${currencySuffix}`;
      price.prevDayPriceAmount = getPrevPrice(+price.dayPriceAmount, price.discount);
      price.prevDayPrice = `${currencySymbol}${price.prevDayPriceAmount}${currencySuffix}`;
      price.savedDayPrice = `${currencySymbol}${getPreparedPrice(price.prevDayPriceAmount - price.dayPriceAmount)}${currencySuffix}`;

      const baseCreateVATPriceParams = {
        currency,
        countryCode,
        displayVAT
      }

      const { vat, price: vatPrice, vatPercent, showVat } = createVATPrice({ amount, ...baseCreateVATPriceParams });
      const { vat: dayVat, price: vatDayPrice } = createVATPrice({ amount: price.dayPriceAmount, ...baseCreateVATPriceParams });
      const { vat: weekVat, price: vatWeekPrice } = createVATPrice({ amount: price.weekPriceAmount, ...baseCreateVATPriceParams });
      const { vat: vatTrial, price: vatTrialPrice } = createVATPrice({ amount: trial_price_amount, ...baseCreateVATPriceParams });

      price.showVat = showVat
      price.vatPercent = vatPercent;

      price.vat = vat;
      price.vatPrice = vatPrice;
      price.vatPriceCombo = createVATComboPrice({ vat, vatPrice, price: price.priceText, displayVAT });

      price.weekVat = weekVat;
      price.vatWeekPrice = vatWeekPrice;
      price.vatWeekPriceCombo = createVATComboPrice({ vat: weekVat, vatPrice: vatWeekPrice, price: price.weekPrice, displayVAT });

      price.dayVat = dayVat;
      price.vatDayPrice = vatDayPrice;
      price.vatDayPriceCombo = createVATComboPrice({ vat: dayVat, vatPrice: vatDayPrice, price: price.dayPrice, displayVAT });

      price.vatTrial = vatTrial;
      price.vatTrialPrice = vatTrialPrice;
      price.vatTrialPriceCombo = createVATComboPrice({ vat: vatTrial, vatPrice: vatTrialPrice, price: price.trialPrice, displayVAT });

      price.perDayPriceAmount = price.trialDays ? +(Number(price.trialPriceAmount) / Number(price.trialDays)).toFixed(2) : price.trialPriceAmount
      
      //@ts-ignore
      const expireAt = dayjs().add(dayjs.duration(period)).add(dayjs.duration(price.trialDays, 'd'));
      //@ts-ignore
      const introExpireAt = dayjs().add(dayjs.duration(price.trialDays, 'd'));

      pds.push({
        ...item,
        ...price,
        period,
        currency,
        expireDate: expireAt.format('LL'),
        introExpireDate: introExpireAt.format('LL'),
        expireAt: dayjs(expireAt).unix(),
        isOneTimePurchase: period === 'ONETIME',
      });
    }

    return pds;
  }, []);
};

export const mappingPricesToUpsaleProducts = (
  { products, countryCode, displayVAT, usdConversionRates }: MappingPricesToUpsaleProductsParams) => {
    const result: Record<string, ProductDetails[]> = {};

    Object.entries(products).forEach(([key, productArray]) => {
      result[key] = productArray.map((item: ProductDetails) => convertSingleProduct(item, countryCode, usdConversionRates, displayVAT));
    });

    return result;
};

const convertSingleProduct = (
  item: ProductDetails,
  countryCode: string,
  usdConversionRates: Record<string, number>,
  displayVAT?: boolean
): ProductDetails => {
  const { oldAmount } = item;

  const convertAmount = convertFromUSDWithVAT(
    +item.amount,
    DEFAULT_CURRENCY_FOR_UPSALE,
    countryCode.toLowerCase(),
    usdConversionRates
  );

  const convertOldPrice = convertFromUSDWithVAT(
    +(oldAmount || 0),
    DEFAULT_CURRENCY_FOR_UPSALE,
    countryCode.toLowerCase(),
    usdConversionRates
  );

  const amount = formatPriceForDisplay(convertAmount, DEFAULT_CURRENCY_FOR_UPSALE);

  const { currencySymbol, currencySuffix } = getCurrencySymbols(DEFAULT_CURRENCY_FOR_UPSALE);

  const price = {
    ...item,
    amount,
    price: `${currencySymbol}${getPreparedPrice(+amount)}${currencySuffix}`,
    priceText: `${currencySymbol}${getPreparedPrice(+amount)}${currencySuffix}`,
    prevPriceAmount: convertOldPrice,
    oldAmount: `${currencySymbol}${getPreparedPrice(convertOldPrice)}${currencySuffix}`,
    savedAmount: `${currencySymbol}${getPreparedPrice(convertOldPrice - +amount)}${currencySuffix}`,
    discount: calculateDiscount(convertOldPrice, convertAmount),
    currency: DEFAULT_CURRENCY_FOR_UPSALE,
    priceUpsaleUSD: item.amount,
  };

  const baseCreateVATPriceParams = {
    currency: DEFAULT_CURRENCY_FOR_UPSALE,
    countryCode,
    displayVAT
  };

  const { vat, price: vatPrice, vatPercent, showVat } = createVATPrice({ amount, ...baseCreateVATPriceParams });

  price.vat = vat;
  price.vatPrice = vatPrice;
  price.vatPriceCombo = createVATComboPrice({ vat, vatPrice, price: price.price, displayVAT });
  price.vatPercent = vatPercent;
  price.showVat = showVat

  return price;
};

export default mappingPricesToProducts;
